import { useId, useState, type FormEvent } from 'react'
import { Clock, Loader2, Plus, Tag, X } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { addTag, addTemporaryTag, getSymbolTagsWithExpiry, removeTag } from '@/lib/api'

function expiryDescription(expiresAt: number | null) {
  if (expiresAt === null) return null
  const date = new Date(expiresAt * 1000)
  if (Number.isNaN(date.getTime())) return null
  const hours = Math.ceil((date.getTime() - Date.now()) / 3_600_000)
  return {
    label: hours <= 0 ? 'expired' : `expires in ${hours >= 24 ? `${Math.ceil(hours / 24)}d` : `${hours}h`}`,
    title: `Expires ${date.toLocaleString()}`,
  }
}

type TagChange =
  | { action: 'add'; tag: string; hours?: number }
  | { action: 'remove'; tag: string }

export function CoinTags({ symbol }: { symbol: string }) {
  const queryClient = useQueryClient()
  const formId = useId()
  const [editing, setEditing] = useState(false)
  const [newTag, setNewTag] = useState('')
  const [isTemporary, setIsTemporary] = useState(false)
  const [temporaryHours, setTemporaryHours] = useState('24')
  const query = useQuery({
    queryKey: ['tags', 'symbol', symbol],
    queryFn: () => getSymbolTagsWithExpiry(symbol),
    refetchInterval: 30_000,
  })

  const mutation = useMutation({
    mutationFn: (change: TagChange) => {
      if (change.action === 'remove') return removeTag(symbol, change.tag)
      return change.hours === undefined
        ? addTag(symbol, change.tag)
        : addTemporaryTag(symbol, change.tag, change.hours)
    },
    onSuccess: (_, change) => {
      void queryClient.invalidateQueries({ queryKey: ['tags'] })
      if (change.action === 'add') {
        setNewTag('')
        setEditing(false)
      }
    },
  })

  const hours = Number(temporaryHours)
  const validHours = Number.isFinite(hours) && hours >= 0.1 && hours <= 168
  const canAdd = newTag.trim().length > 0 && (!isTemporary || validHours)
  const tags = query.data?.tags ?? []

  const handleAdd = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canAdd || mutation.isPending) return
    mutation.mutate({ action: 'add', tag: newTag.trim(), hours: isTemporary ? hours : undefined })
  }

  return (
    <section aria-label={`Tags for ${symbol}`} className="space-y-3 rounded-lg border p-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 text-sm font-semibold">
          <Tag className="h-4 w-4 text-muted-foreground" />
          Tags
        </h4>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={mutation.isPending}
          aria-expanded={editing}
          aria-controls={formId}
          onClick={() => {
            setEditing(!editing)
            mutation.reset()
          }}
        >
          {editing ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          {editing ? 'Cancel' : 'Add tag'}
        </Button>
      </div>

      {query.isPending ? (
        <p className="text-sm text-muted-foreground">Loading tags…</p>
      ) : tags.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {tags.map(({ tag, expires_at }) => {
            const expiry = expiryDescription(expires_at)
            return (
              <Badge
                key={tag}
                variant={expires_at === null ? 'secondary' : 'outline'}
                className="max-w-full gap-1.5 pr-1"
                title={expiry?.title ?? 'Permanent tag'}
              >
                {expiry && <Clock className="h-3 w-3 shrink-0" />}
                <span className="min-w-0 break-all">{tag}</span>
                {expiry && <span className="shrink-0 font-normal text-muted-foreground">{expiry.label}</span>}
                <button
                  type="button"
                  aria-label={`Remove ${tag} from ${symbol}`}
                  title={`Remove ${tag}`}
                  disabled={mutation.isPending}
                  className="shrink-0 rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
                  onClick={() => mutation.mutate({ action: 'remove', tag })}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )
          })}
        </div>
      ) : !query.isError ? (
        <p className="text-sm text-muted-foreground">No tags for this coin.</p>
      ) : null}

      {query.isError && (
        <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-destructive">
          <span>Unable to load tags: {query.error.message}</span>
          <Button type="button" variant="outline" size="sm" disabled={query.isFetching} onClick={() => void query.refetch()}>
            Retry
          </Button>
        </div>
      )}

      {editing && (
        <form id={formId} onSubmit={handleAdd} className="space-y-3 border-t pt-3">
          <div className="flex flex-wrap gap-2">
            <Input
              aria-label={`New tag for ${symbol}`}
              placeholder="Tag name…"
              value={newTag}
              onChange={(event) => setNewTag(event.target.value)}
              disabled={mutation.isPending}
              className="min-w-0 flex-1"
              autoFocus
              required
            />
            <Button type="submit" size="sm" className="h-9" disabled={!canAdd || mutation.isPending}>
              {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Add tag
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <label className="flex items-center gap-2 text-muted-foreground">
              <input
                type="checkbox"
                checked={isTemporary}
                disabled={mutation.isPending}
                onChange={(event) => setIsTemporary(event.target.checked)}
              />
              Temporary tag
            </label>
            {isTemporary && (
              <label className="flex items-center gap-2 text-muted-foreground">
                Expires in
                <Input
                  type="number"
                  min="0.1"
                  max="168"
                  step="any"
                  aria-label="Tag duration in hours"
                  value={temporaryHours}
                  onChange={(event) => setTemporaryHours(event.target.value)}
                  disabled={mutation.isPending}
                  className="h-8 w-20"
                  required
                />
                hours
              </label>
            )}
          </div>
          {isTemporary && !validHours && (
            <p className="text-xs text-destructive">Enter a duration between 0.1 and 168 hours.</p>
          )}
        </form>
      )}

      {mutation.isError && (
        <p role="alert" className="text-sm text-destructive">
          Unable to {mutation.variables?.action === 'remove' ? 'remove' : 'add'} tag: {mutation.error.message}
        </p>
      )}
    </section>
  )
}
